import { type Ref } from 'vue'

export interface CtiService {
  outboundCallFromEverDesk(phone: string): void | Promise<void>

  release(): void

  reload(): void

  setAgentStatus(state: string, breakCode?: string): void
}

export interface CtiServiceWithInit extends CtiService {
  init(): void | Promise<void>
}

// FYI :
// * This interface is commented as vars_ref namming is arguable
// * I decide the type by myself only LOV types are define in the doc
// * Maybe all attributes are optionnals
// * Another definition of vars_ref exists in the documentation but I bet we will not use it
export interface CtiAxialysInMessageVarRef {
  call_duration: number // call duration
  caller_id_num: string // inbound call number
  duration_in_queue: number // wainting duration inside the waiting queue
  event:
    | 'connect_call' // pick up phone and start connection
    | 'delete_call' // end of the phone call
    | 'insert_call' // inbound phone call waiting in the waiting queue
  id_call: string // call technical id
  id_operator: string // operator technical id
  id_queue: string // waiting queue technical id
  rec: string // This is documented as "appel enregistré" but I don't know yet what it is (an URL, a boolean, ... ???)
  statut: 'available' | 'ringing' | 'calling' | 'connected' | 'unavailable' // status of the operator 'statut' is french word for 'status'
  id_group?: string // group of the agent (use to determine which number was called)
  e164_in?: string // called number in e164 norme format (inbound)
  e164_out?: string // called number in e164 format for outbound (notif_out)
  id_session: string // a session id
}

// Same comment as for CtiAxialysInMessageVarRef
export interface CtiAxialysInMessageMessage {
  call_duration: number // call duration
  caller_id_num: string // inbound call number
  id_call: string // call technical id
  id_operator: string // operator technical id
  id_queue: string // waiting queue technical id
  begin_ts: number
  catchup: number
  id_client: string
  service: string
  p: number
  group: string
  numSVI: string
  status: 'available' | 'ringing' | 'calling' | 'connected' | 'unavailable' // status of the operator
}

export interface CtiAxialysInMessageArgs {
  status?: string
  message?: CtiAxialysInMessageMessage
  vars_ref?: CtiAxialysInMessageVarRef
}

export interface CtiAxialysOutMessageArgs {
  token?: string
  phone?: string
}

export interface CtiAxialysInMessage {
  action: string
  args: CtiAxialysInMessageArgs
  e164?: string
  ctxid?: string
}

export interface CtiAxialysOutMessage {
  action: string
  args: CtiAxialysOutMessageArgs
}

export interface CtiGenesysInMessage {
  type: GenesysInteractionType
  category: AuthenticationCategoryType | InteractionCategoryType | NotificationCategoryType | UserActionCategoryType
  interaction?: GenesysConversationInformation
  data: GenesysInData & GenesysRoutingStatus
}

interface GenesysInData {
  provider: string
  token?: string
  expires_in?: string
  url?: string
  status?: GenesysCloudStatus
  textToTranslate?: string
}

export interface CtiGenesysOutMessage {
  type: string
  category?: AuthenticationCategoryType
  data: GenesysOutData
}

interface GenesysOutData {
  number?: string
  type?: string
  provider?: string
  clientId?: string
  token?: string
  embeddableURL?: string
  translatedText?: string
  autoPlace?: boolean
  queueId?: string
}

export interface InteractionContextData {
  CLIENT_DISPLAY_NUMBER: string
  CLIENT_SERVICE_NUMBER: string
  SKILL_ID: string
}

export const enum CtiInteractionTypeEnum {
  'INBOUND' = 'inbound',
  'OUTBOUND' = 'outbound',
}

export const enum LandingPageEnum {
  'TICKET_HANDLING_SCREEN' = 'ticket-handling-screen',
  'SEARCH_CUSTOMER_SCREEN' = 'search-customer-screen',
}

export const landingPageEnumValues: string[] = [LandingPageEnum.TICKET_HANDLING_SCREEN, LandingPageEnum.SEARCH_CUSTOMER_SCREEN]

export const enum CtiStatus {
  'DISCONNECTED' = 'unconnected',
  'AVAILABLE' = 'available',
  'ON_BREAK' = 'on-break',
  'ON_CALL' = 'on-call',
}

export interface CtiStatusOption {
  value: CtiStatus
  label: string
}

export const enum CtiStatusColor {
  'black' = '#3a4b4e',
  'green' = '#00aba5',
  'orange' = '#b25e26',
  'blue' = '#5e9ce9',
}

export const ctiStatusColorMap = new Map<CtiStatus, CtiStatusColor>([
  [CtiStatus.DISCONNECTED, CtiStatusColor.black],
  [CtiStatus.AVAILABLE, CtiStatusColor.green],
  [CtiStatus.ON_BREAK, CtiStatusColor.orange],
  [CtiStatus.ON_CALL, CtiStatusColor.blue],
])

export const enum CtiEventSource {
  'Manual' = 'MANUAL',
  'Automatic' = 'AUTOMATIC',
}

export const enum CtiOutboundCallSource {
  'APPLICATION' = 'APPLICATION',
  'PROVIDER' = 'PROVIDER',
}

export type CallParams = {
  ani?: string
  dnis?: string
  ucid?: string
  extension?: string
  userLogin?: string
  pcLogin?: string
  uui?: string
  parentInteractionId?: string
}

export const enum GenesysInteractionType {
  // noinspection JSUnusedGlobalSymbols -> it's an external contract
  INTERACTION = 'Interaction',
  NOTIFICATION = 'Notification',
  USER_ACTION = 'UserAction',
  AUTHORIZATION = 'Authorization',
}

export type AuthenticationCategoryType = 'initialized' | 'connect' | 'connected' | 'redirect' | 'initTranslation'

export type InteractionCategoryType =
  | 'add'
  | 'change'
  | 'connect'
  | 'disconnect'
  | 'acw'
  | 'deallocate'
  | 'callbackPlaced'
  | 'callbackCallEnded'
  | 'blindTransfer'
  | 'consultTransfer'
  | 'completeConsultTransfer'
export type NotificationCategoryType = 'interactionSelection' | 'chatUpdate' | 'interactionWindow' | 'conversationTranscription' | 'messageUpdate'
export type UserActionCategoryType = 'login' | 'logout' | 'station' | 'status' | 'modal' | 'routingStatus' | 'view'

export interface GenesysConversationInformation {
  // Descriptions of attributes are available in Genesys official documentation: Condensed conversation information
  ani: string
  attributes: Record<string, string> // customInteractionAttributes in framework.js -> config
  calledNumber: string
  connectedTime: string
  dialerContactId: string
  dialerContactListId: string
  dialerCampaignId: string
  direction: string
  displayAddress: string
  disposition: string
  dispositionDurationSeconds: number
  emailSubject: string
  endTime: string
  flagged: boolean
  hasJourneyContext: boolean
  id: string
  interactionDurationSeconds: number
  isConnected: boolean
  isDisconnected: boolean
  isCallback: boolean
  isChat: boolean
  isCoBrowsing: boolean
  isDialer: boolean
  isDialerPreview: boolean
  isEmail: boolean
  isInternal: boolean
  isMessage: boolean
  isSharingScreen: boolean
  isThirdPartyEmail: boolean
  isVoicemail: boolean
  messageType: string
  name: string
  phone: string
  queueName: string
  remoteName: string
  securePause: boolean
  startTime: string
  state: string
  socialUserName: string
  totalAcdDurationSeconds: number
  totalIvrDurationSeconds: number
  uuiData: string
  voicemailId: string
}

export const enum GenesysRoutingStatus {
  // noinspection JSUnusedGlobalSymbols -> it's an external contract
  'OFF_QUEUE' = 'OFF_QUEUE',
  'IDLE' = 'IDLE',
  'INTERACTING' = 'INTERACTING',
  'COMMUNICATING' = 'COMMUNICATING',
  'NOT_RESPONDING' = 'NOT_RESPONDING',
  'UNAVAILABLE' = 'UNAVAILABLE',
}

export const enum GenesysCloudStatus {
  // noinspection JSUnusedGlobalSymbols -> it's an external contract
  'ON_QUEUE' = 'ON_QUEUE',
  'TRAINING' = 'TRAINING',
  'BREAK' = 'BREAK',
  'BUSY' = 'BUSY',
  'MEETING' = 'MEETING',
  'MEAL' = 'MEAL',
  'OUT_OF_OFFICE' = 'OUT_OF_OFFICE',
  'AWAY' = 'AWAY',
  'AVAILABLE' = 'AVAILABLE',
}

export type GenesysStatus = GenesysRoutingStatus | GenesysCloudStatus
export type GenesysAgentDTOV2 = {
  id?: UUID
  name: string
  selfUri: string
  state: GenesysActivityState
  skills: GenesysAgentSkill[]
  username?: string
  version?: number
  acdAutoAnswer?: boolean
  addresses?: Array<{
    address?: string
    display?: string
    mediaType?: GenesysMediaType
    type?: GenesysContactType
    extension?: string
    countryCode?: string
    integration?: string
  }>
  division: {
    id: UUID
    name: string
    selfUri: string
  }
  email: string
  primaryContactInfo: Array<{
    address: string
    mediaType?: GenesysMediaType
    type?: GenesysContactType
  }>
  presence?: GenesysPresenceData
}

export type GenesysAgentSkill = {
  id: string
  name: string
  proficiency?: number
  selfUri?: string
  skillUri?: string
  state?: GenesysActivityState
}

type GenesysActivityState = 'active' | 'inactive' | 'deleted'
type GenesysMediaType = 'PHONE' | 'EMAIL' | 'SMS'
type GenesysContactType = 'PRIMARY' | 'WORK' | 'WORK2' | 'WORK3' | 'WORK4' | 'HOME' | 'MOBILE' | 'MAIN' | 'OTHER'

export type GenesysPresenceData = {
  source?: string
  presenceDefinition?: {
    id: string
    systemPresence: string
    selfUri: string
  }
  message?: string
  modifiedDate?: string
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface CtiFive9InMessage {}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface CtiFive9OutMessage {}

export interface OpenCallCtiEvent {
  callNumber: string
  customerId: number | null
  caseId?: number | null
}

export type Five9AdtConfig = {
  providerName: string
  myCallsTodayEnabled: boolean
  myChatsTodayEnabled: boolean
  myEmailsTodayEnabled: boolean
  showContactInfo: boolean
}

export interface Five9Interface {
  CrmSdk: {
    crmApi: () => {
      click2dial: (params: { click2DialData: Five9Click2DialData }) => void
      registerApi: (config: { getAdtConfig: () => Promise<Five9AdtConfig> }) => void
    }
    interactionApi: () => {
      subscribe: (handlers: {
        callAccepted: (params: Five9CallParams) => Promise<void>
        callRejected: (params: Five9CallParams) => void
        callEnded: (params: Five9CallParams) => void
        callFinished: (params: Five9CallFinishedParams) => Promise<void>
        callStarted: (params: Five9CallParams) => void
      }) => void
      getCrm: (params: { interactionId: string }) => Promise<Five9ContactField[]>
      getDispositions: (params: { interactionType: string; interactionId: string }) => Promise<Five9DispositionList[]>
    }
    presenceApi: () => {
      subscribe: (handlers: { presenceChanged: (params: Five9PresenceChangedParams) => void }) => void
      getChannels: () => Promise<string>
      getNotReadyCodes: () => Promise<string[]>
    }
  }
}

export enum Five9CallType {
  AGENT = 'AGENT',
  AGENT_PREVIEW = 'AGENT_PREVIEW',
  AUTODIAL = 'AUTODIAL',
  INBOUND = 'INBOUND',
  INBOUND_VOICEMAIL = 'INBOUND_VOICEMAIL',
  INTERNAL = 'INTERNAL',
  INTERNAL_VOICEMAIL = 'INTERNAL_VOICEMAIL',
  OUTBOUND = 'OUTBOUND',
  OUTBOUND_PREVIEW = 'OUTBOUND_PREVIEW',
  OUTBOUND_VOICEMAIL = 'OUTBOUND_VOICEMAIL',
  QUEUE_CALLBACK = 'QUEUE_CALLBACK',
  TEST = 'TEST',
  TEST_PREVIEW = 'TEST_PREVIEW',
  VISUAL = 'VISUAL',
}

export enum Five9CallState {
  NONE = 'NONE',
  QUEUED = 'QUEUED',
  DIALING = 'DIALING',
  RINGING_ON_OUR_SIDE = 'RINGING_ON_OUR_SIDE',
  OFFERED = 'OFFERED',
  RINGING_ON_OTHER_SIDE = 'RINGING_ON_OTHER_SIDE',
  TALKING = 'TALKING',
  ON_HOLD = 'ON_HOLD',
  PARKED = 'PARKED',
  DISABLED = 'DISABLED',
  WRAP_UP = 'WRAP_UP',
  FINISHED = 'FINISHED',
  CONFERENCE_PARTICIPANT_RINGING = 'CONFERENCE_PARTICIPANT_RINGING',
  CONFERENCE_PARTICIPANT_TALKING = 'CONFERENCE_PARTICIPANT_TALKING',
  CONFERENCE_PARTICIPANT_CONSULTING = 'CONFERENCE_PARTICIPANT_CONSULTING',
  CONFERENCE_PARTICIPANT_ON_HOLD = 'CONFERENCE_PARTICIPANT_ON_HOLD',
}

export type Five9Click2DialData = {
  clickToDialNumber: string
  screenpopC2DSearch?: boolean
  preselectedCampaignName?: string
  crmObject: Five9CrmObject
}

export type Five9Disposition = {
  id: string
  name: string
}

export type Five9DispositionList = {
  interactionType: Five9InteractionType
  interactionId: string
}

export type Five9CrmObjectField = {
  displayName: string
  value: string
}

export type Five9CrmObject = {
  id: string
  type: string
  label: string
  name: string
  isWho: boolean
  isWhat: boolean
  fields?: Five9CrmObjectField[]
  metadata?: object
}

export type Five9DataType =
  | 'BOOLEAN'
  | 'CURRENCY'
  | 'DATE'
  | 'DATE_TIME'
  | 'EMAIL'
  | 'NUMBER'
  | 'PERCENT'
  | 'PHONE'
  | 'STRING'
  | 'TIME'
  | 'TIME_PERIOD'
  | 'UNDEFINED'
  | 'URL'

export type Five9Cav = {
  id: string
  group: string
  name: string
  type: Five9DataType
  value: string
}

export type Five9ContactField = {
  id: string
  name: string
  dataType: Five9DataType
  value: string | number
}

export type Five9CallLogData = {
  who: Five9CrmObject
  what: Five9CrmObject
  subject: string
  comment: string
  comments: string
  sessionID: string
  disposition: Five9Disposition
  dispositionName: string
  startTime: number
  duration: number
  handleTime: number
  wrapTime: number
  talkTime: number
  holdTime: number
  cavList: Five9Cav[]
  crmList: Five9ContactField[]
}

export type Five9CallData = {
  interactionId: string
  sessionId: string
  callId: string
  agent: string
  agentId: string
  agentExtension: string
  agentName: string
  domainId: string
  domainName: string
  locale: string
  ani: string
  dnis: string
  number: string
  contactDisplayName: string
  click2DialData?: Five9Click2DialData
  callType: Five9CallType
  callState: Five9CallState
  campaignName?: string
  callbackNumber?: string
  campaignId?: string
  subject?: string
  comment?: string
  transcript?: string
  comments?: string
  disposition?: Five9Disposition
  dispositionName?: string
  transferredAgent?: string
  transferredQueue?: string
  startTime?: number
  duration?: number
  distributionMode: string
  type: Five9InteractionType
  skillName: string
  skillId: string
  interactionSubType?: string
  callbackId?: string
}

export enum Five9LoginState {
  /** Agent needs to select station */
  SELECT_STATION = 'SELECT_STATION',
  /** Agent must accept maintenance notice */
  ACCEPT_NOTICE = 'ACCEPT_NOTICE',
  /** Agent needs to select active skills */
  SELECT_SKILLS = 'SELECT_SKILLS',
  /** Agent is ready for work */
  WORKING = 'WORKING',
  /** CRM Adapter tries to restart session until session is ready */
  RELOGIN = 'RELOGIN',
}

export enum Five9InteractionType {
  CALL = 'Call',
  PREVIEW_CALL = 'PreviewCall',
  QUEUE_CALLBACK = 'QueueCallback',
  SKILL_VOICEMAIL = 'SkillVoicemail',
  CHAT = 'Chat',
  EMAIL = 'Email',
}

export type Five9LoginStateChangedParams = {
  state: Five9LoginState
}

export type Five9CallParams = {
  callData: Five9CallData
}

export enum Five9FinishCallReason {
  AFTER_CALL_WORK_TIME_EXPIRED = 'AFTER_CALL_WORK_TIME_EXPIRED',
  REJECTED = 'REJECTED',
}

export type Five9CallFinishedParams = {
  callData: Five9CallData
  callLogData: Five9CallLogData
  reason: Five9FinishCallReason
}

export type Five9PresenceChangedParams = {
  presence: Five9Presence
  context: Five9PresenceEventReason
}

export type Five9Presence = {
  isReady: boolean
  channelIds: string[]
  notReadyCodeId: string
  elapsedTime: string
}

export type Five9PresenceEventReason =
  /** Supervisor has changed your Ready for Voicemail state. */
  | 'Supervisor'
  /** You are automatically being set to the Not Ready state because a call was offered to you and was not answered within the timeout period. Set yourself to the Ready state to resume taking calls. */
  | 'AutoNotReady'
  /** A call was offered to you, but your station does not appear to be connected. You are being set to Not Ready, so you can restart your station before taking another call. */
  | 'HookStateError'
  /** A call was offered to you, but you are not logged in at a station. You are being set to Not Ready, so no calls can be routed to you. You should not go Ready when you are not logged in at a station except when you plan to receive test calls. */
  | 'NoStation'
  /** State update has been initiated by the agent. */
  | 'Agent'
  /** State has been reset to some initial value on login/reconnect. */
  | 'Initial'
  /** State has been changed due to permission removal. */
  | 'PermissionRemoved'
  /** State has been changed due to reason code disabling. */
  | 'ReasonCodeDisabled'
  /** You have exceeded the after-call work time limit. */
  | 'AfterCallWorkTimeExpired'
  /** You have automatically been set to the Not Ready state because a preview call was offered to you and you neither made a call nor set a decline disposition within the timeout period. Please set yourself to the Ready state to resume taking calls. */
  | 'PreviewEndNotReady'
  /** You have been forcibly switched to the Task Completion state as there are unfinished tasks. */
  | 'TaskCompletion'
  /** You are automatically being set to the Not Ready state because an interaction was offered to you and was not answered within the timeout period. Set yourself to the Ready state to resume taking interactions. */
  | 'InteractionNotAnswered'
  /** The active call has been ended. As the result, the delayed state change has been applied. */
  | 'CallFinished'

export type Five9Config = {
  timezone: string
  lang: string
}

export type Five9MetadataParams = {
  agentId?: string
  tenantId?: string
}

export type Five9ApiErrorStatus = {
  errorCode: Five9ApiErrorCode
  errorMessage: string
}

export type Five9ApiErrorCode = 'Generic' | 'InteractionNotFound' | 'NoData'

export interface AgentModel {
  providerLogin?: string | null
  extension?: string
  desktopLogin?: string
  status?: CtiStatus
}

export enum VoiceProvider {
  'UNIFIED_CTI' = 'unified-cti',
  'AXIALYS' = 'axialys',
  'GENESYS' = 'genesys',
  'VOICEBOT' = 'voicebot',
  'FIVE_9' = 'five9',
  'NONE' = 'none',
}

export enum VoiceConfigurationTabEnum {
  'GENERAL' = 'general',
  'INBOUND' = 'inbound',
  'OUTBOUND' = 'outbound',
}

export enum ProviderConnectionType {
  'TOKEN' = 'token',
  'MANUAL' = 'manual',
  'SSO' = 'SSO',
}

export enum ProviderUIType {
  'FRAMED' = 'framed',
  'SIDE' = 'side',
}

export enum LandingPageForVoicebotOnly {
  'NO_HANDLING_SCREEN' = 'no-handling-screen',
}

export enum CustomerPhoneFieldEnum {
  'HOMEPHONE' = 'homePhone',
  'WORKPHONE' = 'workPhone',
  'MOBILEPHONE' = 'mobilePhone',
  'OTHERPHONE' = 'otherPhone',
}

export type CallConfig = {
  configType: VoiceConfigurationTabEnum
  callConfig?: InboundCallConfigurationDTOV2 | OutboundCallConfigurationDTOV2
  configId?: UUID
}

export type VoiceConfigurationDTOV1 = {
  activeProvider: VoiceProvider
}

export type VoiceConfigurationDTOV2 = {
  defaultProvider: VoiceProvider
}

export type ProviderGeneralConfigurationDTOV2 = {
  id: UUID | null
  provider: VoiceProvider
  providerConfiguration?: ProviderSpecificConfiguration | null
  connectionType: ProviderConnectionType | null
  connection?: {
    login?: string
    password?: string
  }
  uiIntegrationType?: ProviderUIType
  landingPage: LandingPageEnum | LandingPageForVoicebotOnly | null
  fallbackSkill: string | null
  iframeUrl?: string
  creationCustomerWhenNotFoundEnabled: boolean
  attachLatestCustomerWhenMultipleFoundEnabled?: boolean
  creationCustomerPhoneFieldName: CustomerPhoneFieldEnum | null
  space: UUID
}

export type ProviderSpecificConfiguration = {
  genesys?: GenesysIFrameConfiguration
  axialys?: AxialysConfigurationDTOV2
  unifiedCti?: UnifiedCtiIFrameConfigurationDTOV2
  five9?: Five9ConfigurationDTOV2
}

export interface AxialysConfigurationDTOV2 {
  login?: string
  password?: string
}

export interface GenesysIFrameConfiguration {
  clientId: string
  apiUrl: string
  loginUrl: string
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface UnifiedCtiIFrameConfigurationDTOV2 {}

export interface Five9ConfigurationDTOV2 {
  sdkUrl: string
  customerRefName: string
  customerRefGroup: string
}

export type CallConfigurationDTOV2 = {
  id?: UUID
  providerConfiguration: UUID
  summary?: string
  code: string
  phoneNumber?: string
  space?: UUID
  active?: boolean
}

export type InboundCallConfigurationDTOV2 = {
  skill: string
} & CallConfigurationDTOV2

export type OutboundCallConfigurationDTOV2 = {
  defaultSkill: string
  requiredSkills?: Array<string>
} & CallConfigurationDTOV2

export interface CallDTOV1 {
  callId: string
  provider: VoiceProvider
  groupId: string | null
  recordingLink: string | null
  serviceNumber: string | null
  callConfiguration: UUID
  customer: VoiceCustomer
  type: CtiInteractionTypeEnum
  ticketId: number
}

export interface CallInteractionDTOV2 {
  callId: string | null
  providerConfiguration: UUID
  callConfiguration?: UUID
  customer?: VoiceCustomer
  agent?: VoiceAgent
  escalateToLiveAgent?: boolean
  externalOutboundCall?: boolean
  duration?: string
  messages?: CallMessage[]
  caseId?: number
  type: CtiInteractionTypeEnum
}

export interface CallMessage {
  text: string
  type: CtiInteractionTypeEnum
}

export interface CallRequestBody {
  callId: string | null
  provider: VoiceProvider
  serviceNumber: string | null
  callConfiguration: UUID | null | undefined
  groupId: string | null
  customer: VoiceCustomer
  type: CtiInteractionTypeEnum
  agent: VoiceAgent
  ticketId?: number | null
}

export interface OutboundCallRequestBody {
  outboundCallNumberId: UUID | null
  phoneNumber: string
  ticketId: number | null
  customerId: number | null
  externalOutboundCall?: boolean
  callId?: string
}

export interface CallDurationUpdateDTOV1 {
  provider: string
  duration: CallDuration
}

export interface PatchCallInteractionDTOV2 {
  recordingLink?: string
  duration: CallDuration
}

type DateUnit = `${number}Y` | `${number}M` | `${number}D`
type TimeUnit = `${number}H` | `${number}M` | `${number}S`

type DatePart = DateUnit | `${DateUnit}${DateUnit}` | `${DateUnit}${DateUnit}${DateUnit}`
type TimePart = TimeUnit | `${TimeUnit}${TimeUnit}` | `${TimeUnit}${TimeUnit}${TimeUnit}`

export type CallDuration = `P${number}W` | `P${DatePart}` | `P${DatePart}T${TimePart}` | `PT${TimePart}`

export interface VoiceCustomer {
  customerNumber: string | null
  userToUserInformation?: string
  externalReference?: string | null
  customerId?: number | null
  foundMultipleCustomers?: boolean | null
}

export interface VoiceAgent {
  ctiLogin?: string | null
  extension?: string
  desktopLogin?: string
}

export type ModalConfirm = {
  configType: string
  status: Ref<boolean>
  config: InboundCallConfigurationDTOV2 | OutboundCallConfigurationDTOV2
}

export interface IFrameStyle {
  narrowWidth: string
  wideWidth: string
  height: string
  innerPadding: string
}

export interface CtiCallInfo {
  requestInfo: CallRequestBody | CallInteractionDTOV2 | null
  isCallV2: boolean
  customerResultLength: number
}

export interface NewCallV2Param {
  providerConfiguration: UUID
  customer: VoiceCustomer
  type: CtiInteractionTypeEnum
  callId: string | null
  callConfiguration?: UUID
  caseId?: number
  agent?: AgentModel
  escalateToLiveAgent?: boolean
  externalOutboundCall?: boolean
  duration?: string
  messages?: CallMessage[]
}
